import React from "react";
import Dropdown from "../../components/ui/dropdown";
import { getOpsManagers } from "../../components/ui/OpsManagers";
import { createRef, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Location as TQSLocation } from "../../models/location";
import { AgreementReturnModel } from "../../models/agreement";
import Select, { SingleValue } from "react-select";
import CopyButton from "../../components/ui/CopyButton";
import { components } from "react-select";
import { useAppDispatch, useAppSelector } from "../../reducers/store";
import { getQualityAlertsByLocations } from "../../reducers/qualityAlertSlice";
import { getAgreementsBySupplier } from "../../reducers/supplierSlice";

export interface Rep {
  id: number;
  email: string;
  type: string;
  firstName: string;
  lastName: string;
  location: string;
  cell: string;
  photoAttachment: string;
  primaryContact: boolean;
}

export function Team() {
  const dispatch = useAppDispatch();
  const selectedSupplier = useAppSelector((state) => state.supplier.selectedSupplier);
  const selectedProducingFacilities = useAppSelector((state) => state.supplier.selectedProducingFacilities);
  const storeAgreements = useAppSelector((state) => state.supplier.agreements);
  const isLoading = useAppSelector((state) => state.supplier.isLoading);
  const error = useAppSelector((state) => state.supplier.error);
  const qualityAlerts = useAppSelector((state) => state.qa.models);
  const qaLoading = useAppSelector((state) => state.qa.loading);
  const qaError = useAppSelector((state) => state.qa.error);

  const [searchParams] = useSearchParams();
  const modalRef = useRef<any>();
  const [allAssignedReps, setAllAssignedReps] = useState<Rep[]>([]);
  const [agreements, setAgreements] = useState<AgreementReturnModel[]>([] as AgreementReturnModel[]);
  const [selectedLocation, setSelectedLocation] = useState<TQSLocation | null>({} as TQSLocation);
  const [filteredLocations, setFilteredLocations] = useState<TQSLocation[]>([] as TQSLocation[]);
  const [allLocations, setAllLocations] = useState<TQSLocation[]>([]);
  const [locations, setLocations] = useState<TQSLocation[]>([] as TQSLocation[]);
  const [selectedFilteredLocation, setSelectedFilteredLocation] = useState<TQSLocation[]>([]);
  const [oem, setOem] = useState<{ id: number; name: string }[]>([]);
  const [selectedOems, setSelectedOems] = useState<{ id: number; name: string }[]>([]);
  const [search, setSearch] = useState<string>("");
  const [filterModalOpen, setFilterModalOpen] = useState<boolean>(false);

  useEffect(() => {
    const getData = async () => {
      const supplierId = selectedSupplier?.id;
      if (supplierId) {
        try {
          await dispatch(getAgreementsBySupplier(supplierId));
        } catch (error) {
          console.error('Error fetching agreements:', error);
        }
      }
    };

    getData();
  }, [selectedSupplier]);

  useEffect(() => {
    if (!isLoading && !error && storeAgreements.length > 0) {
      setAgreements(storeAgreements);
      processAgreements(storeAgreements);
      setSearch("");
    }
  }, [storeAgreements, isLoading, error]);

  useEffect(() => {
    processAgreements(agreements);
  }, [selectedProducingFacilities]);

  useEffect(() => {
    searchLocations(search);
  }, [selectedOems, search]);

  const processAgreements = async (agreements: AgreementReturnModel[]) => {
    // Create new objects instead of modifying existing ones
    const processedAgreements = agreements.map(a => ({
      ...a,
      agreement: {
        ...a.agreement,
        location: {
          ...a.agreement.location,
          agreement: JSON.parse(JSON.stringify(a.agreement))
        }
      }
    }));

    const data: TQSLocation[] = processedAgreements
      .filter((a) => {
        return (
          !a.producingFacility ||
          a.producingFacility.some((p) =>
            selectedProducingFacilities.map((s) => s.id).includes(p),
          )
        );
      })
      .map((a) => {
        return a.agreement.location;
      })
      .filter((value, index, self) => self.indexOf(value) === index)
      // Resolved off the unfiltered reps, so a liaison search cannot change it
      .map((location) => ({
        ...location,
        opsManagers: getOpsManagers(location),
      }));

    if (data.length > 0 && selectedSupplier?.id) {
      try {
        await dispatch(getQualityAlertsByLocations({
          locationIds: data.map((l) => l.id),
          supplierId: selectedSupplier.id
        }));
      } catch (error) {
        console.error('Error fetching quality alerts:', error);
      }
    }

    const locationId = searchParams.get("locationId");
    if (locationId) {
      const selectedLocation = data.find((l) => l.id === parseInt(locationId));
      if (selectedLocation) {
        setSelectedLocation(selectedLocation);
      }
    }

    setLocations(data);
    setAllLocations(data);
    setFilteredLocations(data);
    setOEM(data);
  };

  useEffect(() => {
    if (!qaLoading && !qaError && Object.keys(qualityAlerts).length > 0) {
      const updatedLocations = allLocations.map(location => ({
        ...location,
        qualityAlerts: Object.values(qualityAlerts).filter(qa => qa.locationId === location.id)
      }));
      setAllLocations(updatedLocations);
      setLocations(updatedLocations);
      setFilteredLocations(updatedLocations);
    }
  }, [qualityAlerts, qaLoading, qaError]);

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      height: "45px",
      minHeight: "45px",
      borderRadius: "6px",
      border: "1px solid #D1D5DB",
      borderColor: state.isFocused || state.isHovered ? '#2B30B9' : '#D1D5DB', // tqsYellow for hover and focus, gray-300 otherwise
      '&:hover': {
        borderColor: '#D1D5DB', // Hover border color
      },
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
  };

  const setOEM = (data: TQSLocation[]) => {
    const oems = data
      .filter((l) => l.oem)
      .map((l) => ({ id: l.oem.id, name: l.oem.name }))
      .filter(
        (value, index, self) =>
          self.findIndex((v) => v.id === value.id) === index,
      )
      .sort((a, b) => a.name.localeCompare(b.name));

    setOem(oems);
    setSelectedOems(oems);
  };
  const searchLocations = (
    value: string,
    locationData: TQSLocation[] = locations,
  ) => {
    setSearch(value);
    const oemFilter = locationData.filter((l) =>
      selectedOems.map((o) => o.id).includes(l.oem?.id ?? 19),
    );
    const data = JSON.parse(JSON.stringify(oemFilter)).filter(
      (l: TQSLocation) => l.name.toLowerCase().includes(value.toLowerCase()),
    );
    setFilteredLocations(data);
  };

  useEffect(() => {
    allLocations.forEach((location) => {
      location.agreement?.assignedReps.forEach((rep) => {
        if (rep && rep.representative) {
          if (
            location.agreement?.primaryRepresentative === rep.representative.id
          ) {
            rep.representative.primaryContact = true;
          } else {
            rep.representative.primaryContact = false;
          }
        }
      });
    });

    const allReps: Rep[] = allLocations.flatMap(
      (location) =>
        location.agreement?.assignedReps.map((rep) => rep.representative) ?? [],
    );
    setAllAssignedReps(allReps);
  }, [allLocations]);

  useEffect(() => {
    setSelectedFilteredLocation(allLocations);
  }, [allLocations]);

  const handleLocationChange = (newValue: TQSLocation | null) => {
    if (newValue) {
      const selectedName = newValue.name;
      setSelectedLocation(newValue);

      // Find the matching location by name
      const matchingLocation = allLocations.find(
        (location) => location.name === selectedName,
      );

      if (matchingLocation) {
        // Set the matched location in selectedFilteredLocation
        setSelectedFilteredLocation([matchingLocation]);
      } else {
        // If no match is found, keep all locations
        setSelectedFilteredLocation(allLocations);
      }
    } else {
      // If no location is selected (or selection is cleared), reset to all locations
      setSelectedLocation(null);
      setSelectedFilteredLocation(allLocations);
    }
  };

  function emailTqs(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    window.open("mailto:help@teamqs.com");
  }

  // Function to filter selectedFilteredLocation based on search input for reps
  const filterLocationsByReps = (searchInput: string) => {
    // Convert the search input to lowercase for case-insensitive comparison
    const lowercasedSearchInput = searchInput.toLowerCase();

    // Determine locations to search: all locations if no location is selected, or just the selected one
    const locationsToSearch =
      selectedLocation && selectedLocation.id
        ? [selectedLocation]
        : allLocations;

    // Filter the locations based on whether they have reps that match the search input
    const filteredLocations = locationsToSearch
      .map((location) => {
        // Filter the assigned reps based on the search input
        const filteredReps = location.agreement?.assignedReps.filter((rep) => rep?.representative).filter((rep) => {
          const fullName = `${rep.representative.firstName} ${rep.representative.lastName}`;
          const email = rep.representative.email;

          // Check if the full name or email includes the search input
          const matches =
            fullName.toLowerCase().includes(lowercasedSearchInput) ||
            email.toLowerCase().includes(lowercasedSearchInput);

          return matches;
        });

        // Check whether any of the location's ops managers matches the search input
        const opsManagerMatches = getOpsManagers(location).some(
          (manager) =>
            `${manager.firstName} ${manager.lastName}`
              .toLowerCase()
              .includes(lowercasedSearchInput) ||
            (manager.email ?? "").toLowerCase().includes(lowercasedSearchInput),
        );

        // If there are matching reps or a matching ops manager, keep the location
        if ((filteredReps && filteredReps.length > 0) || opsManagerMatches) {
          return {
            ...location,
            agreement: {
              ...location.agreement,
              assignedReps: filteredReps ?? [],
            },
          };
        }

        return null;
      })
      .filter((location) => location !== null) as TQSLocation[];

    // Update the state with the filtered locations
    setSelectedFilteredLocation(filteredLocations);
  };

  // Trigger the filter function when the search input, selected location, or all locations change
  useEffect(() => {
    filterLocationsByReps(search);
  }, [search, selectedLocation, allLocations]);

  //  A custom comoonent for the dropdown indicator, just for the chevrons
  const DropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <div className="flex flex-col items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="h-3 w-4 text-[#6c7075]"
          >
            <path
              fillRule="evenodd"
              d="M11.47 7.72a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 1 1-1.06 1.06L12 9.31l-6.97 6.97a.75.75 0 0 1-1.06-1.06l7.5-7.5Z"
              clipRule="evenodd"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="h-3 w-4 text-[#6c7075]"
          >
            <path
              fillRule="evenodd"
              d="M12.53 16.28a.75.75 0 0 1-1.06 0l-7.5-7.5a.75.75 0 0 1 1.06-1.06L12 14.69l6.97-6.97a.75.75 0 1 1 1.06 1.06l-7.5 7.5Z"
              clipRule="evenodd"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        </div>
      </components.DropdownIndicator>
    );
  };
  //  Custom Clear icon for the react-select component
  const ClearIndicator = (props: any) => {
    return (
      <components.ClearIndicator {...props}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="h-5 w-5 cursor-pointer text-gray-400"
        >
          <path
            fillRule="evenodd"
            d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z"
            clipRule="evenodd"
          />
        </svg>
      </components.ClearIndicator>
    );
  };

  return (
    <div className="max-h-100 flex h-[100%] w-[100%] items-center justify-center text-tqsBlack">
      <div className="flex h-full w-full flex-col items-center">
        <div className="flex w-[90%] flex-row justify-start"></div>
        <div className="flex w-[90%] flex-row justify-center rounded-[8px] bg-white pb-4 pt-6">
          <div className="flex w-full flex-row items-center">
            <div className="ml-8 flex w-[60%] flex-col">
              <span className="text-[20px] font-semibold text-tqsBlack">
                Liaison Name
              </span>

              <div className="relative mt-[4px] flex w-full items-center">
                <div className="relative w-full">
                  {/* Magnifying Glass Icon */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-70"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
                      clipRule="evenodd"
                    />
                  </svg>

                  {/* Input Field */}
                  <input
                    className="text-tqs placeholder-gray h-[45px] mb-[1px] w-full rounded-md border border-gray-300 bg-white p-1 pl-[40px] pr-[40px] text-[15px] font-medium focus:shadow-[0_0_0_1px_#2684FF] hover:border-[#D1D5DB] focus:outline-none"
                    type="text"
                    placeholder="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)} // Update the search term state
                  />

                  {/* "X" Icon */}
                  {search && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="#212322"
                      className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 transform cursor-pointer text-gray-400"
                      onClick={() => setSearch("")} // Clear the search term when the icon is clicked
                    >
                      <path
                        fillRule="evenodd"
                        d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </div>
            </div>
            <div className="ml-3 mr-4 flex w-[60%] flex-col">
              <span className="text-[20px] font-semibold text-tqsBlack">
                Service Location
              </span>
              <div className="mt-[2px] h-[45px]">
                {allLocations.length > 0 ? (
                  <div
                    style={{ position: "relative", zIndex: 10 }}
                    className="h-full"
                  >
                    <Select
                      value={selectedLocation}
                      onChange={handleLocationChange}
                      options={allLocations}
                      className="z-10 mr-2 text-[14px] font-medium xs:text-[4px] sm:text-[6px] md:text-[10px] lg:text-[15px]"
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.name}
                      isClearable={true}
                      placeholder="Select a location"
                      components={{ DropdownIndicator, ClearIndicator }}
                      styles={customStyles}
                    />
                  </div>
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <span className="loading loading-dots loading-sm text-tqsYellow"></span>
                  </div>
                )}
              </div>
            </div>
            <div className="ml-2 mr-4 flex w-full flex-col justify-center">
              <div className="flex h-[70%] w-full flex-row rounded-[8px] border border-gray-200 max-h-[80px] items-center justify-center p-[8px]">
                <div className="flex w-full flex-row items-center justify-between gap-4">
                  <div className="flex h-auto w-auto flex-col">
                    <div className="h-[46px] w-[46px] rounded-full border border-gray-200">
                      <img
                        src="/dashboard/help.png"
                      />
                    </div>
                  </div>
                  <div className="flex h-[70px] w-full flex-col overflow-hidden text-ellipsis">
                    <span className="font-semibold text-tqsBlack text-[20px] overflow-hidden whitespace-nowrap text-ellipsis h-[30px]">
                      Need Help?
                    </span>
                    <p className="text-[12px] font-semibold text-[#8e8e93] h-[40px] line-clamp-2">
                      We can help. Contact us 24/7 to reach a member of our Team.
                    </p>
                  </div>

                  <div className="flex w-full flex-col items-center h-[70px]">
                    <button
                      onClick={emailTqs}
                      className="flex flex-row items-center justify-center rounded-[8px] bg-[#8e8e93] p-[4px] font-semibold text-white w-[150px] h-[30px] text-[12px] mt-[10px]"
                    >
                      help@teamqs.com{" "}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="ml-2 h-[12px] w-[12px]"
                      >
                        <path d="M1.5 8.67v8.58a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3V8.67l-8.928 5.493a3 3 0 0 1-3.144 0L1.5 8.67Z" />
                        <path d="M22.5 6.908V6.75a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3v.158l9.714 5.978a1.5 1.5 0 0 0 1.572 0L22.5 6.908Z" />
                      </svg>
                    </button>
                    <p className="text-sm text-[#8e8e93] h-[30px] flex items-end pb-[4px]">
                      <span
                        style={{
                          textDecoration:
                            "underline text-[12px] !important ",
                        }}
                      >
                        <CopyButton text={"1.866.568.8326"} />
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="justify-left max-h-[1000px] w-[90%] items-center justify-center overflow-auto">
          {selectedFilteredLocation.length > 0 && (
            <div className="mt-4 flex w-full flex-col gap-4">
              {selectedFilteredLocation
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((location) => (
                  <Dropdown
                    key={location.id}
                    location={location}
                    open={!!search || !!selectedLocation?.id}
                  />
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
